import React from 'react';
import clsx from 'clsx';
import { Concept, Vocabulary } from '@semantical/modules/vocabulary';
import type { OntologyRef, VocabularyRef, DatasetRef } from '@semantical/modules/semantical-core';
import ModelingLanguageSelector from '@semantical/components/ModelingLanguageSelector';
import LabelingContextSelector from '@semantical/components/LabelingContextSelector';
import DatasetSelector from '@semantical/components/DatasetSelector';

/** Runtime ontology reference with optional domain label for UI display */
interface OntologyRefForUI extends OntologyRef {
    readonly domainLabel?: string;
}

type Props = {
    // State values
    knowledgeGraphUri: string;
    editingMode: 'TBox' | 'ABox';
    showChat: boolean;
    darkMode: boolean;
    addingInline: boolean;
    draftTopConceptName: string;
    proposedConcepts: Concept[];
    vocabulary: Vocabulary;

    // Modeling Language (diagram-level) - ontology selection
    selectedModelingLanguageIri: string | null;
    /** Ontologies already imported by the KG (shown first in dropdown) */
    kgOntologies?: OntologyRefForUI[];
    /** Ontologies from BC's SemanticLayer (shown second in dropdown) */
    contextOntologies: OntologyRefForUI[];
    /** All domain ontologies (shown after separator in dropdown) */
    domainOntologies: OntologyRefForUI[];
    onModelingLanguageChange: (iri: string | null) => void;

    // Labeling Context (diagram-level) - vocabulary from BC's UbiquitousLanguage
    selectedLabelingContextIri: string | null;
    availableLabelingContexts: VocabularyRef[];
    onLabelingContextChange: (uri: string | null) => void;

    // Dataset (diagram-level) - linked KnowledgeGraph from BC's datasets
    selectedDatasetId: string | null;
    availableDatasets: DatasetRef[];
    onDatasetChange: (id: string | null) => void;

    // Callbacks
    onToggleView: () => void;
    onOpenVocabularyTurtleModal: () => void;
    onOpenDomainModelTurtleModal: () => void;
    onOpenDiagramTurtleModal: () => void;
    onExportToJson: () => void;
    onExportToTurtle: () => void;
    onCreateNewDiagram: () => void;
    onShowExamplePicker: () => void;
    onToggleChat: () => void;
    onSetEditingMode: (mode: 'TBox' | 'ABox') => void;
    onStartInlineAdd: () => void;
    onConfirmInlineAdd: () => void;
    onCancelInlineAdd: () => void;
    onDraftTopConceptNameChange: (name: string) => void;
    onAddConcept: (concept: Concept) => void;
    onRejectConcept: (concept: Concept) => void;
    onDropConceptClass: (conceptUri: string) => void;
    hasConceptInDiagram: (conceptUri: string) => boolean;
    draggingConceptUri: string | null;
    onOpenBoundedContextInfo: () => void;

    // Whether to show "Load Example" button (depends on available examples)
    hasExamples?: boolean;
};

export default class TitleBar extends React.Component<Props> {
    inlineNameInput: React.RefObject<HTMLInputElement | null>;

    constructor(props: Props) {
        super(props);
        this.inlineNameInput = React.createRef<HTMLInputElement>();
    }

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (this.props.addingInline && !prevProps.addingInline) {
            const inp = this.inlineNameInput.current;
            if (inp) {
                inp.focus();
                inp.select();
            }
        }
    }

    render() {
        // Common button class patterns for Tailwind
        const btnBase = "px-3 py-[5px] rounded-[5px] cursor-pointer text-[11px] font-medium transition-all duration-150 font-sans border border-slate-200 dark:border-transparent";
        const btnAction = clsx(btnBase, "bg-slate-100 dark:bg-white/[0.08] text-slate-600 dark:text-white/75 ml-1.5");
        const btnToggleOn = clsx(btnBase, "bg-accent-teal-light dark:bg-accent-muted text-teal-700 dark:text-accent/90 border-accent-teal dark:border-transparent ml-1.5");
        const btnToggleOff = clsx(btnBase, "bg-slate-100 dark:bg-white/[0.06] text-slate-400 dark:text-white/50 ml-1.5");
        const segmentBase = "px-3 py-[5px] cursor-pointer text-[11px] font-medium transition-all duration-150 font-sans rounded-none m-0 border-none border-r border-slate-200 dark:border-white/10";
        const segmentActive = clsx(segmentBase, "bg-accent-teal-light dark:bg-[rgba(100,180,255,0.25)] text-accent-teal dark:text-[rgba(100,180,255,1)]");
        const segmentInactive = clsx(segmentBase, "bg-white dark:bg-white/[0.04] text-slate-400 dark:text-white/40");
        const btnSmall = clsx(btnBase, "px-2 py-[3px] text-[10px] ml-1");
        const btnSmallAccept = clsx(btnSmall, "bg-emerald-100 dark:bg-accent/20 text-emerald-600 dark:text-accent/90");
        const btnSmallReject = clsx(btnSmall, "bg-red-100 dark:bg-error/15 text-red-600 dark:text-[rgba(255,150,150,0.9)]");

        const {
            knowledgeGraphUri,
            editingMode,
            showChat,
            darkMode,
            addingInline,
            draftTopConceptName,
            proposedConcepts,
            vocabulary,
            selectedModelingLanguageIri,
            kgOntologies,
            contextOntologies,
            domainOntologies,
            onModelingLanguageChange,
            selectedLabelingContextIri,
            availableLabelingContexts,
            onLabelingContextChange,
            selectedDatasetId,
            availableDatasets,
            onDatasetChange,
            draggingConceptUri,
            onToggleView,
            onOpenVocabularyTurtleModal,
            onOpenDomainModelTurtleModal,
            onOpenDiagramTurtleModal,
            onExportToJson,
            onExportToTurtle,
            onCreateNewDiagram,
            onShowExamplePicker,
            onToggleChat,
            onSetEditingMode,
            onStartInlineAdd,
            onConfirmInlineAdd,
            onCancelInlineAdd,
            onDraftTopConceptNameChange,
            onAddConcept,
            onRejectConcept,
            onDropConceptClass,
            hasConceptInDiagram,
            onOpenBoundedContextInfo,
            hasExamples,
        } = this.props;

        return (
            <div id="domain" className={clsx(
                "shrink-0 p-5 overflow-hidden border-b transition-colors duration-300",
                "bg-white text-slate-700 border-slate-200 shadow-[0_1px_3px_rgba(100,120,140,0.08)]",
                "dark:bg-surface-dark dark:text-white dark:border-accent-border dark:shadow-none",
                editingMode === 'ABox' ? "basis-auto" : "basis-[160px]"
            )}>
                <h1 className="flex items-center gap-2 m-0 text-lg font-medium flex-wrap text-slate-800 dark:text-white">
                    <span>Semantical&nbsp;</span>
                    <span className="text-[11px] font-mono text-slate-400 dark:text-white/40 max-w-[300px] truncate" title={knowledgeGraphUri}>
                        {knowledgeGraphUri}
                    </span>
                    <img
                        src="/src/assets/graph.png"
                        alt="graph"
                        className="h-8 w-8 cursor-pointer invert-[0.3] dark:invert"
                        onClick={onToggleView}
                    />
                    <button className={btnAction} onClick={onOpenVocabularyTurtleModal}>Vocabulary TTL</button>
                    <button className={btnAction} onClick={onOpenDomainModelTurtleModal}>{editingMode === 'ABox' ? 'KG TTL' : 'Model TTL'}</button>
                    <button className={btnAction} onClick={onOpenDiagramTurtleModal}>Diagram TTL</button>
                    <button className={btnAction} onClick={onExportToJson}>Export</button>
                    <button className={btnAction} onClick={onExportToTurtle}>Export TTL</button>
                    <button className={btnAction} onClick={(e) => {
                        if (e.ctrlKey || e.metaKey) {
                            localStorage.clear();
                            window.location.reload();
                        } else {
                            onCreateNewDiagram();
                        }
                    }} title="New diagram (Ctrl/Cmd+click to clear all data)">New</button>
                    {hasExamples && (
                        <button className={btnAction} onClick={onShowExamplePicker}>Load Example</button>
                    )}
                    <button className={btnAction} onClick={onOpenBoundedContextInfo}>Context Info</button>
                    <button className={showChat ? btnToggleOn : btnToggleOff} onClick={onToggleChat}>
                        Chat
                    </button>
                    <div className="inline-flex rounded-[5px] overflow-hidden ml-1.5 border border-slate-200 dark:border-white/15">
                        <button
                            className={editingMode === 'TBox' ? segmentActive : segmentInactive}
                            onClick={() => onSetEditingMode('TBox')}
                        >
                            Schema
                        </button>
                        <button
                            className={clsx(editingMode === 'ABox' ? segmentActive : segmentInactive, "!border-r-0")}
                            onClick={() => onSetEditingMode('ABox')}
                        >
                            Data
                        </button>
                    </div>
                    <ModelingLanguageSelector
                        selectedOntologyIri={selectedModelingLanguageIri}
                        kgOntologies={kgOntologies}
                        contextOntologies={contextOntologies}
                        domainOntologies={domainOntologies}
                        onOntologyChange={onModelingLanguageChange}
                        darkMode={darkMode}
                    />
                    <LabelingContextSelector
                        selectedVocabularyUri={selectedLabelingContextIri}
                        availableVocabularies={availableLabelingContexts}
                        onVocabularyChange={onLabelingContextChange}
                        darkMode={darkMode}
                    />
                    <DatasetSelector
                        selectedDatasetId={selectedDatasetId}
                        availableDatasets={availableDatasets}
                        onDatasetChange={onDatasetChange}
                        darkMode={darkMode}
                    />
                </h1>
                {editingMode === 'TBox' && (
                    <>
                        <h2 className="inline-flex items-center gap-2 text-slate-800 dark:text-white">
                            Concepts
                            <button className={clsx(btnBase, "bg-slate-100 dark:bg-white/[0.08] text-slate-600 dark:text-white/75 px-2 py-[3px]")} onClick={onStartInlineAdd} disabled={addingInline}>+</button>
                        </h2>
                        <div id="topConcepts" className="ml-4">
                            {addingInline && (
                                <span className={clsx(
                                    "inline-block p-2.5 rounded-[5px] my-2.5 mx-0 cursor-pointer transition-all duration-200",
                                    "bg-slate-100 text-slate-700 border border-slate-200 shadow-[0_1px_2px_rgba(100,120,140,0.06)]",
                                    "dark:bg-[#304040] dark:text-white dark:border-transparent dark:shadow-none"
                                )}>
                                    <input
                                        ref={this.inlineNameInput}
                                        className="bg-transparent border-none text-inherit font-inherit outline-none py-1 px-1.5 placeholder:text-slate-400 dark:placeholder:text-[#bbbbbb]"
                                        placeholder="New concept name"
                                        value={draftTopConceptName}
                                        onChange={(e) => onDraftTopConceptNameChange(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') onConfirmInlineAdd(); if (e.key === 'Escape') onCancelInlineAdd(); }}
                                    />
                                </span>
                            )}
                            {vocabulary.getConcepts().map((concept) => {
                                const isInDiagram = hasConceptInDiagram(concept.uri);
                                const isDragging = draggingConceptUri === concept.uri;
                                return (
                                    <span key={concept.uri}
                                          className={clsx(
                                              "inline-block p-2.5 rounded-[5px] my-2.5 mx-0 cursor-pointer transition-all duration-200",
                                              "bg-slate-100 text-slate-700 border border-slate-200 shadow-[0_1px_2px_rgba(100,120,140,0.06)]",
                                              "hover:bg-accent-teal-light hover:border-accent-teal hover:shadow-[0_2px_4px_rgba(59,130,152,0.12)] hover:-translate-y-0.5",
                                              "dark:bg-[#304040] dark:text-white dark:border-transparent dark:shadow-none",
                                              "dark:hover:bg-[#405050] dark:hover:border-transparent",
                                              isDragging && "opacity-50 scale-95",
                                              isInDiagram && "!bg-slate-50 !text-slate-400 !border-slate-200 opacity-50 cursor-not-allowed transform-none hover:transform-none hover:!bg-slate-50 hover:!border-slate-200 hover:shadow-none dark:!bg-[#252530] dark:!text-white/50 dark:!border-transparent"
                                          )}
                                          onClick={() => !isInDiagram && onDropConceptClass(concept.uri)}
                                          title={isInDiagram ? "Already in diagram" : "Click to add to diagram"}>
                                        {concept.prefLabel}
                                    </span>
                                );
                            })}
                            {proposedConcepts.map((concept) => (
                                <span key={concept.uri} className={clsx(
                                    "inline-block p-2.5 rounded-[5px] my-2.5 mx-0 cursor-pointer transition-all duration-200",
                                    "bg-slate-100 text-slate-700 border border-slate-200 shadow-[0_1px_2px_rgba(100,120,140,0.06)]",
                                    "hover:bg-accent-teal-light hover:border-accent-teal hover:shadow-[0_2px_4px_rgba(59,130,152,0.12)] hover:-translate-y-0.5",
                                    "dark:bg-[#304040] dark:text-white dark:border-transparent dark:shadow-none",
                                    "dark:hover:bg-[#405050] dark:hover:border-transparent"
                                )}>{concept.prefLabel}
                                    <button className={btnSmallAccept} data-concept-id={concept.uri} onClick={() => onAddConcept(concept)}>✓</button>
                                    <button className={btnSmallReject} title="Reject" onClick={() => onRejectConcept(concept)}>✕</button>
                                </span>
                            ))}
                        </div>
                    </>
                )}
            </div>
        );
    }
}
